import React, { useState, useEffect, useMemo } from "react";
import {
  Table, Input, Button, Modal, Form, Switch, Select,
  Tooltip, message, Row, Col, Tag,
} from "antd";
import {
  SearchOutlined, PlusOutlined, EditOutlined,
  UserAddOutlined, FileTextOutlined, FileExcelOutlined,
  LockOutlined, EyeOutlined, EyeInvisibleOutlined,
  MailOutlined, UserOutlined, ShopOutlined,
  ApartmentOutlined, CheckCircleOutlined,
} from "@ant-design/icons";
import { apiRequest } from "../../../utilities/apiMethod";
import { useThemeStore } from "../../../store/useThemeStore";
import { colSearch, StatusTag, actionBtnStyle, exportCSV, exportExcel } from "../../Master/masterUtils";

/* ── Avatar helpers ─────────────────────────────────────────────────────────── */
const PALETTE = ['#6366f1','#2563eb','#7c3aed','#e11d48','#0d9488','#ea580c','#059669','#d97706','#0891b2','#64748b'];
function avatarColor(str) {
  if (!str) return '#6b7280';
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return PALETTE[Math.abs(h) % PALETTE.length];
}
function getInitials(name) {
  if (!name) return '?';
  const p = name.trim().split(/\s+/);
  return p.length > 1 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
}

/* ── Compact form field ─────────────────────────────────────────────────────── */
function CF({ name, label, rules, children, span }) {
  return (
    <Col span={span ?? 6}>
      <Form.Item name={name} rules={rules} style={{ marginBottom: 10 }}
        label={<span style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>}
      >
        {children}
      </Form.Item>
    </Col>
  );
}

const SH = { style: { borderRadius: 7, height: 34 } };

export default function RegisterUser() {
  const { themeColor } = useThemeStore();

  const [data,         setData]         = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pagination,   setPagination]   = useState({ current: 1, pageSize: 10 });

  const [modalOpen, setModalOpen] = useState(false);
  const [editRec,   setEditRec]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [showPwd,   setShowPwd]   = useState(false);
  const [form]                    = Form.useForm();

  const [roleData,       setRoleData]       = useState([]);
  const [storeData,      setStoreData]      = useState([]);
  const [employeesData,  setEmployeesData]  = useState([]);
  const [divisionData,   setDivisionData]   = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [loadingDepts,   setLoadingDepts]   = useState(false);
  const [currentDivIds,  setCurrentDivIds]  = useState([]);

  useEffect(() => {
    fetchUsers();
    Promise.all([fetchRoles(), fetchStores(), fetchEmployees(), fetchDivisions()]);
  }, []);

  async function fetchUsers() {
    setLoading(true);
    try {
      const res = await apiRequest("GET", "Users");
      setData(res?.IsSuccess && Array.isArray(res.Data) ? res.Data : []);
    } finally { setLoading(false); }
  }
  async function fetchRoles()     { try { const r = await apiRequest("GET", "Roles");                    setRoleData(r?.Data ?? []);      } catch { setRoleData([]); } }
  async function fetchStores()    { try { const r = await apiRequest("GET", "Store/getallstores");       setStoreData(r?.Data ?? []);     } catch { setStoreData([]); } }
  async function fetchEmployees() { try { const r = await apiRequest("GET", "Employee");                 setEmployeesData(r?.Data ?? []); } catch { setEmployeesData([]); } }
  async function fetchDivisions() { try { const r = await apiRequest("GET", "Category/getalldivisions"); setDivisionData(r?.Data ?? []);  } catch { setDivisionData([]); } }

  async function fetchDepartments(divisionIds) {
    if (!divisionIds?.length) { setDepartmentData([]); return; }
    setLoadingDepts(true);
    try {
      const results = await Promise.all(divisionIds.map(id => apiRequest("GET", `Category/getAllDepartmentsByDivision/${id}`)));
      const all    = results.flatMap(r => r?.IsSuccess && Array.isArray(r.Data) ? r.Data : []);
      const unique = all.filter((d, i, a) => a.findIndex(x => x.Department === d.Department) === i);
      setDepartmentData(unique);
    } catch { setDepartmentData([]); }
    finally  { setLoadingDepts(false); }
  }

  /* ── Derived stats ──────────────────────────────────────────────────────── */
  const totalActive    = data.filter(r => r.IsActive).length;
  const totalInactive  = data.filter(r => !r.IsActive).length;
  const totalApprovers = data.filter(r => r.ApproverRights).length;

  /* ── Filtered data ──────────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    let result = data;
    if (statusFilter === "active")   result = result.filter(r => r.IsActive);
    if (statusFilter === "inactive") result = result.filter(r => !r.IsActive);
    const q = search.trim().toLowerCase();
    if (!q) return result;
    return result.filter(r =>
      r.UserName?.toLowerCase().includes(q) ||
      r.Name?.toLowerCase().includes(q) ||
      r.Email?.toLowerCase().includes(q) ||
      r.RoleName?.toLowerCase().includes(q)
    );
  }, [data, search, statusFilter]);

  /* ── Modal handlers ─────────────────────────────────────────────────────── */
  function openAdd() {
    setEditRec(null); setDepartmentData([]); setShowPwd(false);
    setCurrentDivIds([]); form.resetFields(); setModalOpen(true);
  }
  function openEdit(rec) {
    setEditRec(rec); setShowPwd(false);
    const divIds = rec.Division?.split(",").filter(Boolean) ?? [];
    setCurrentDivIds(divIds);
    if (divIds.length) fetchDepartments(divIds); else setDepartmentData([]);
    form.setFieldsValue({
      name:           rec.Name           ?? "",
      userName:       rec.UserName        ?? "",
      password:       "",
      email:          rec.Email           ?? "",
      employeeID:     rec.EmployeeID      ?? null,
      roleId:         rec.RoleId          ?? null,
      storeId:        rec.StoreId?.split(",").filter(Boolean)    ?? [],
      divisionId:     divIds,
      departmentId:   rec.Department?.split(",").filter(Boolean) ?? [],
      isActive:       rec.IsActive        ?? false,
      approverRights: rec.ApproverRights  ?? false,
    });
    setModalOpen(true);
  }
  function closeModal() { setModalOpen(false); setSaving(false); }

  function onValuesChange(changed) {
    if ("divisionId" in changed) {
      const ids = changed.divisionId ?? [];
      setCurrentDivIds(ids);
      form.setFieldValue("departmentId", []);
      fetchDepartments(ids);
    }
  }

  function handleMultiChange(field, value, allValues) {
    if (value.includes("__all__")) {
      const cur = form.getFieldValue(field) ?? [];
      form.setFieldValue(field, cur.length === allValues.length ? [] : allValues);
    }
    if (field === "divisionId") {
      const ids = value.filter(v => v !== "__all__");
      setCurrentDivIds(ids);
      form.setFieldValue("departmentId", []);
      fetchDepartments(ids);
    }
  }

  async function onFinish(values) {
    setSaving(true);
    try {
      const isEdit  = !!editRec;
      const payload = {
        name:           values.name,
        userName:       values.userName.trim(),
        email:          values.email,
        employeeID:     values.employeeID,
        roleId:         values.roleId,
        storeId:        values.storeId?.filter(v => v !== "__all__").join(",")      ?? "",
        division:       values.divisionId?.filter(v => v !== "__all__").join(",")   ?? "",
        department:     values.departmentId?.filter(v => v !== "__all__").join(",") ?? "",
        isLocked:       false,
        isActive:       !!values.isActive,
        approverRights: !!values.approverRights,
      };
      if (!isEdit || values.password?.trim()) payload.password = values.password;
      const res = await apiRequest(
        isEdit ? "PUT" : "POST",
        isEdit ? `Users/Register/${editRec.UserLoginId}` : "Users/Register",
        payload
      );
      if (res?.IsSuccess) {
        message.success(isEdit ? "User updated." : "User registered.");
        closeModal(); fetchUsers();
      } else {
        message.error(res?.DisplayMessage || res?.displayMessage || "Something went wrong.");
      }
    } catch { message.error("Request failed."); }
    finally  { setSaving(false); }
  }

  /* ── Expandable row — stores · divisions · departments ──────────────────── */
  const expandedRowRender = (rec) => {
    const stores = rec.StoreId?.split(",").filter(Boolean)    ?? [];
    const divs   = rec.Division?.split(",").filter(Boolean)   ?? [];
    const depts  = rec.Department?.split(",").filter(Boolean) ?? [];
    if (!stores.length && !divs.length && !depts.length) return <span style={{ color: "#c4c7cd", fontSize: 12 }}>No access details available</span>;

    return (
      <div style={{ display: "flex", gap: 24, padding: "8px 4px", flexWrap: "wrap" }}>
        {stores.length > 0 && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 5 }}>
              <ShopOutlined style={{ color: themeColor, fontSize: 11 }} />
              <span style={{ fontSize: 10, fontWeight: 800, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>Stores ({stores.length})</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {stores.map(s => (
                <Tag key={s} style={{ borderRadius: 20, fontSize: 11, fontWeight: 600, padding: "1px 9px", margin: 0, border: "none", background: themeColor + "12", color: themeColor }}>{s}</Tag>
              ))}
            </div>
          </div>
        )}
        {divs.length > 0 && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 5 }}>
              <ApartmentOutlined style={{ color: "#7c3aed", fontSize: 11 }} />
              <span style={{ fontSize: 10, fontWeight: 800, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>Divisions ({divs.length})</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {divs.map(d => (
                <Tag key={d} style={{ borderRadius: 20, fontSize: 11, fontWeight: 600, padding: "1px 9px", margin: 0, border: "none", background: "#f3f4ff", color: "#7c3aed" }}>{d}</Tag>
              ))}
            </div>
          </div>
        )}
        {depts.length > 0 && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 5 }}>
              <CheckCircleOutlined style={{ color: "#0d9488", fontSize: 11 }} />
              <span style={{ fontSize: 10, fontWeight: 800, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em" }}>Departments ({depts.length})</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {depts.map(d => (
                <Tag key={d} style={{ borderRadius: 20, fontSize: 11, fontWeight: 600, padding: "1px 9px", margin: 0, border: "none", background: "#f0fdfa", color: "#0d9488" }}>{d}</Tag>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ── Columns ────────────────────────────────────────────────────────────── */
  const columns = [
    {
      title: "S.No", key: "sno", width: 56, align: "center",
      render: (_, __, i) => (
        <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 600 }}>
          {(pagination.current - 1) * pagination.pageSize + i + 1}
        </span>
      ),
    },
    {
      title: "User", key: "user", minWidth: 180,
      sorter: (a, b) => (a.Name ?? a.UserName ?? "").localeCompare(b.Name ?? b.UserName ?? ""),
      ...colSearch("Name", "Name", themeColor),
      render: (_, rec) => {
        const name  = rec.Name || rec.UserName || "";
        const color = avatarColor(name);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 10, flexShrink: 0,
              background: `linear-gradient(135deg, ${color}dd, ${color})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 2px 6px ${color}44`,
            }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: "#fff", letterSpacing: "0.02em" }}>
                {getInitials(name)}
              </span>
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</p>
              <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>@{rec.UserName}</p>
            </div>
          </div>
        );
      },
    },
    {
      title: "Email", dataIndex: "Email", key: "Email", minWidth: 180,
      ...colSearch("Email", "Email", themeColor),
      render: v => (
        <span style={{ fontSize: 12, color: "#374151", display: "flex", alignItems: "center", gap: 5 }}>
          <MailOutlined style={{ color: "#c4c7cd", fontSize: 11, flexShrink: 0 }} />
          {v || "—"}
        </span>
      ),
    },
    {
      title: "Role", dataIndex: "RoleName", key: "RoleName", width: 140,
      ...colSearch("RoleName", "Role", themeColor),
      render: v => v
        ? <span style={{ fontSize: 11.5, fontWeight: 700, color: themeColor, background: themeColor + "12", padding: "3px 10px", borderRadius: 20 }}>{v}</span>
        : <span style={{ color: "#d1d5db" }}>—</span>,
    },
    {
      title: "Access", key: "access", width: 110, align: "center",
      render: (_, rec) => {
        const sc = rec.StoreId?.split(",").filter(Boolean).length   ?? 0;
        const dc = rec.Division?.split(",").filter(Boolean).length  ?? 0;
        if (!sc && !dc) return <span style={{ color: "#d1d5db", fontSize: 12 }}>—</span>;
        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            {sc > 0 && <span style={{ fontSize: 11, fontWeight: 600, color: themeColor }}>{sc} store{sc > 1 ? "s" : ""}</span>}
            {dc > 0 && <span style={{ fontSize: 11, fontWeight: 600, color: "#7c3aed" }}>{dc} div{dc > 1 ? "s" : ""}</span>}
          </div>
        );
      },
    },
    {
      title: "Status", key: "status", width: 150, align: "center",
      filters: [{ text: "Active", value: true }, { text: "Inactive", value: false }],
      onFilter: (val, rec) => rec.IsActive === val,
      render: (_, rec) => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, flexWrap: "wrap" }}>
          <StatusTag active={rec.IsActive} />
          {rec.ApproverRights && (
            <span style={{ fontSize: 10, fontWeight: 700, color: "#7c3aed", background: "#f3f4ff", padding: "1px 7px", borderRadius: 20, whiteSpace: "nowrap" }}>
              Approver
            </span>
          )}
          {rec.IsLocked && (
            <Tooltip title="Account Locked">
              <LockOutlined style={{ fontSize: 11, color: "#ef4444" }} />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Action", key: "action", width: 70, align: "center", fixed: "right",
      render: (_, rec) => (
        <Tooltip title="Edit" placement="top">
          <button
            onClick={() => openEdit(rec)}
            style={actionBtnStyle(themeColor)}
            onMouseEnter={e => Object.assign(e.currentTarget.style, { background: "#f0f4ff", borderColor: themeColor + "55" })}
            onMouseLeave={e => Object.assign(e.currentTarget.style, { background: "#f9fafb", borderColor: "#e5e7eb" })}
          >
            <EditOutlined style={{ fontSize: 13 }} />
          </button>
        </Tooltip>
      ),
    },
  ];

  const exportRows = filtered.map((r, i) => ({
    "S.No": i + 1, "Name": r.Name ?? r.UserName, "Username": r.UserName,
    "Email": r.Email, "Role": r.RoleName,
    "Stores": r.StoreId ?? "", "Divisions": r.Division ?? "", "Departments": r.Department ?? "",
    "Approver": r.ApproverRights ? "Yes" : "No",
    "Status": r.IsActive ? "Active" : "Inactive",
  }));

  /* ── Status filter pill buttons ─────────────────────────────────────────── */
  const filterBtns = [
    { key: "all",      label: "All",      count: data.length },
    { key: "active",   label: "Active",   count: totalActive },
    { key: "inactive", label: "Inactive", count: totalInactive },
  ];

  return (
    <div style={{ padding: "4px 0" }}>

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div style={{
        background: "#fff", borderRadius: 14, border: "1px solid #f0f1f3",
        boxShadow: "0 1px 6px rgba(0,0,0,0.04)", padding: "12px 18px",
        marginBottom: 10, display: "flex", alignItems: "center",
        justifyContent: "space-between", gap: 16, flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0, background: themeColor,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 4px 14px ${themeColor}44`, transition: "background 0.3s",
          }}>
            <UserAddOutlined style={{ fontSize: 17, color: "#fff" }} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827" }}>Register User</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 3, flexWrap: "wrap" }}>
              {[
                { val: data.length,    label: "Total",     color: themeColor },
                { val: totalActive,    label: "Active",    color: "#16a34a" },
                { val: totalInactive,  label: "Inactive",  color: "#dc2626" },
                { val: totalApprovers, label: "Approvers", color: "#7c3aed" },
              ].map((s, i) => (
                <React.Fragment key={s.label}>
                  {i > 0 && <span style={{ width: 1, height: 12, background: "#e5e7eb", display: "inline-block" }} />}
                  <span style={{ fontSize: 13, fontWeight: 700, color: s.color }}>{s.val}</span>
                  <span style={{ fontSize: 13, color: "#6b7280" }}>{s.label}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
        <Button
          icon={<PlusOutlined />} onClick={openAdd}
          style={{ height: 38, borderRadius: 9, fontWeight: 600, fontSize: 13, background: themeColor, borderColor: themeColor, color: "#fff", boxShadow: `0 2px 8px ${themeColor}44`, flexShrink: 0 }}
        >
          Add User
        </Button>
      </div>

      {/* ── Table card ───────────────────────────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f1f3", boxShadow: "0 1px 6px rgba(0,0,0,0.04)", overflow: "hidden" }}>

        {/* Toolbar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid #f0f1f3", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Input
              placeholder="Search name, email, role…"
              prefix={<SearchOutlined style={{ color: "#c4c7cd" }} />}
              value={search} onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, current: 1 })); }}
              allowClear style={{ width: 260, borderRadius: 8, height: 34 }}
            />
            {/* Status filter pills */}
            <div style={{ display: "flex", gap: 4, background: "#f4f5f7", borderRadius: 8, padding: 3 }}>
              {filterBtns.map(btn => {
                const active = statusFilter === btn.key;
                return (
                  <button
                    key={btn.key}
                    onClick={() => { setStatusFilter(btn.key); setPagination(p => ({ ...p, current: 1 })); }}
                    style={{
                      padding: "3px 10px", borderRadius: 6, border: "none", cursor: "pointer",
                      fontSize: 12, fontWeight: 700, transition: "all 0.15s",
                      background: active ? "#fff" : "transparent",
                      color: active ? themeColor : "#9ca3af",
                      boxShadow: active ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                    }}
                  >
                    {btn.label}
                    <span style={{ marginLeft: 5, fontSize: 10, fontWeight: 800, color: active ? themeColor : "#c4c7cd" }}>
                      {btn.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>{filtered.length} record{filtered.length !== 1 ? "s" : ""}</span>
            <Button icon={<FileTextOutlined />} onClick={() => exportCSV(exportRows, "users.csv")} style={{ height: 30, borderRadius: 7, fontWeight: 600, borderColor: "#22c55e", color: "#16a34a", fontSize: 11 }}>CSV</Button>
            <Button icon={<FileExcelOutlined />} onClick={() => exportExcel(exportRows, "users.xlsx")} style={{ height: 30, borderRadius: 7, fontWeight: 600, borderColor: "#16a34a", color: "#15803d", fontSize: 11 }}>Excel</Button>
          </div>
        </div>

        {/* Table */}
        <Table
          rowKey="UserLoginId"
          columns={columns}
          dataSource={filtered}
          loading={loading}
          size="small"
          scroll={{ x: 900 }}
          expandable={{
            expandedRowRender,
            rowExpandable: rec =>
              !!(rec.StoreId?.length || rec.Division?.length || rec.Department?.length),
            expandRowByClick: false,
          }}
          rowClassName={() => "user-table-row"}
          onChange={pag => setPagination({ current: pag.current, pageSize: pag.pageSize })}
          pagination={{
            ...pagination, total: filtered.length,
            showSizeChanger: true, pageSizeOptions: ["10", "20", "50"],
            showTotal: (t, r) => `Showing ${r[0]}–${r[1]} of ${t}`,
            style: { padding: "8px 14px", margin: 0 },
          }}
        />
      </div>

      {/* ── Modal ────────────────────────────────────────────────────────── */}
      <Modal
        open={modalOpen} onCancel={closeModal} footer={null}
        width={860} centered destroyOnClose
        styles={{ body: { padding: "14px 20px 20px" } }}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: "#f4f5f7", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {editRec ? <EditOutlined style={{ fontSize: 13, color: themeColor }} /> : <UserAddOutlined style={{ fontSize: 13, color: themeColor }} />}
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>
                {editRec ? "Edit User" : "Register New User"}
              </p>
              <p style={{ margin: "1px 0 0", fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>
                {editRec ? `Editing — ${editRec.UserName}` : "Fill in the details below to create a new user account"}
              </p>
            </div>
          </div>
        }
      >
        <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false} onValuesChange={onValuesChange}>

          {/* Row 1 */}
          <Row gutter={12}>
            <CF name="name" label="Full Name" rules={[{ required: true, message: "Required" }]}>
              <Input placeholder="John Smith" prefix={<UserOutlined style={{ color: "#c4c7cd", fontSize: 12 }} />} {...SH} />
            </CF>
            <CF name="userName" label="Username" rules={[{ required: true, message: "Required" }]}>
              <Input placeholder="john.smith" {...SH} />
            </CF>
            <CF
              name="password"
              label={editRec ? "New Password (blank = keep)" : "Password"}
              rules={editRec ? [{ min: 6, message: "Min 6 chars" }] : [{ required: true, message: "Required" }, { min: 6, message: "Min 6 chars" }]}
            >
              <Input
                type={showPwd ? "text" : "password"}
                placeholder={editRec ? "Leave blank to keep" : "Min 6 characters"}
                prefix={<LockOutlined style={{ color: "#c4c7cd", fontSize: 12 }} />}
                suffix={
                  <button type="button" onClick={() => setShowPwd(p => !p)}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", color: "#9ca3af" }}>
                    {showPwd ? <EyeOutlined style={{ fontSize: 13 }} /> : <EyeInvisibleOutlined style={{ fontSize: 13 }} />}
                  </button>
                }
                {...SH}
              />
            </CF>
            <CF name="email" label="Email Address" rules={[{ required: true, message: "Required" }, { type: "email", message: "Invalid email" }]}>
              <Input placeholder="john@company.com" prefix={<MailOutlined style={{ color: "#c4c7cd", fontSize: 12 }} />} {...SH} />
            </CF>
          </Row>

          {/* Row 2 */}
          <Row gutter={12}>
            <CF name="employeeID" label="Employee" rules={[{ required: true, message: "Required" }]}>
              <Select placeholder="Select employee" allowClear showSearch optionFilterProp="label" style={{ height: 34, width: "100%" }}
                options={employeesData.map(e => ({ value: e.ID, label: `${e.FirstName} ${e.LastName}` }))} />
            </CF>
            <CF name="roleId" label="Role" rules={[{ required: true, message: "Required" }]}>
              <Select placeholder="Select role" allowClear showSearch optionFilterProp="label" style={{ height: 34, width: "100%" }}
                options={roleData.map(r => ({ value: r.RoleId, label: r.RoleName }))} />
            </CF>
            <CF name="storeId" label="Store" rules={[{ required: true, message: "Required" }]} span={12}>
              <Select mode="multiple" placeholder="Select store(s)" allowClear showSearch optionFilterProp="label" maxTagCount="responsive" style={{ width: "100%" }}
                onChange={val => handleMultiChange("storeId", val, storeData.map(s => s.StoreCode))}
                options={[{ value: "__all__", label: "— Select All —" }, ...storeData.map(s => ({ value: s.StoreCode, label: s.StoreName }))]} />
            </CF>
          </Row>

          {/* Row 3 */}
          <Row gutter={12} align="bottom">
            <CF name="divisionId" label="Division" rules={[{ required: true, message: "Required" }]}>
              <Select mode="multiple" placeholder="Select division(s)" allowClear showSearch optionFilterProp="label" maxTagCount="responsive" style={{ width: "100%" }}
                onChange={val => handleMultiChange("divisionId", val, divisionData.map(d => d.Division))}
                options={[{ value: "__all__", label: "— Select All —" }, ...divisionData.map(d => ({ value: d.Division, label: d.DivisionName }))]} />
            </CF>
            <CF name="departmentId" label="Department" rules={[{ required: true, message: "Required" }]}>
              <Select mode="multiple" placeholder={currentDivIds.length ? "Select department(s)" : "Select division first"}
                allowClear showSearch optionFilterProp="label" maxTagCount="responsive"
                loading={loadingDepts} disabled={!currentDivIds.length} style={{ width: "100%" }}
                onChange={val => handleMultiChange("departmentId", val, departmentData.map(d => d.Department))}
                options={[{ value: "__all__", label: "— Select All —" }, ...departmentData.map(d => ({ value: d.Department, label: d.DepartmentName }))]} />
            </CF>
            <Col span={6}>
              <Form.Item style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 12px", borderRadius: 8, border: "1px solid #f0f1f3", background: "#fafafa", height: 34 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Active</span>
                  <Form.Item name="isActive" valuePropName="checked" noStyle initialValue={false}><Switch size="small" /></Form.Item>
                </div>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 12px", borderRadius: 8, border: "1px solid #f0f1f3", background: "#fafafa", height: 34 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Approver</span>
                  <Form.Item name="approverRights" valuePropName="checked" noStyle initialValue={false}><Switch size="small" /></Form.Item>
                </div>
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: "flex", gap: 10, paddingTop: 10, borderTop: "1px solid #f0f1f3", marginTop: 2 }}>
            <Button onClick={closeModal} style={{ flex: 1, height: 38, borderRadius: 8, fontWeight: 600 }}>Cancel</Button>
            <Button htmlType="submit" loading={saving}
              style={{ flex: 2, height: 38, borderRadius: 8, fontWeight: 700, background: themeColor, borderColor: themeColor, color: "#fff", transition: "background 0.3s" }}>
              {editRec ? "Save Changes" : "Register User"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
